// Offline-tolerant delivery for the SMALL set of events that matter most in
// the field. Trails are played in parks, woods and stone buildings — exactly
// where connectivity dies mid-session — and posthog-js's retry queue lives
// in memory, so anything captured offline is lost when the tab closes.
//
// Model: callers use capturePlayerEvent() instead of posthog.capture for the
// events worth queueing. Online → straight through. Offline → persisted to a
// small localStorage queue with the original timestamp, flushed on the next
// boot and whenever connectivity returns; posthog's `timestamp` capture
// option keeps the event's true time in the data.
//
// Consent: nothing is queued for an opted-out player (checked at enqueue
// time), and the flush path still goes through posthog.capture's own gate —
// so opting out between enqueue and flush also suppresses delivery.
import posthog from 'posthog-js';

const KEY = 'mt-event-outbox';
const MAX_QUEUED = 50;
const MAX_AGE_MS = 48 * 60 * 60 * 1000;

interface QueuedEvent {
  /** Epoch ms at capture time. */
  t: number;
  event: string;
  properties?: Record<string, unknown>;
}

function read(): QueuedEvent[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as QueuedEvent[]) : [];
  } catch {
    return [];
  }
}

function write(queue: QueuedEvent[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(queue.slice(-MAX_QUEUED)));
  } catch {
    // Storage unavailable — degrade to fire-and-forget.
  }
}

const isOptedOut = (): boolean => {
  try {
    return posthog.has_opted_out_capturing();
  } catch {
    return false;
  }
};

/**
 * posthog.capture with an offline fallback queue. Use for field-critical
 * events (trail progress, diagnostics, freeze reports) — NOT for high-volume
 * noise; the queue is deliberately small.
 */
export function capturePlayerEvent(
  event: string,
  properties?: Record<string, unknown>
): void {
  try {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      if (isOptedOut()) return;
      write([...read(), { t: Date.now(), event, properties }]);
      return;
    }
    posthog.capture(event, properties);
  } catch {
    // Monitoring must never break the app.
  }
}

/** Deliver anything queued from an offline stretch. */
export function flushOutbox(): void {
  try {
    // Still offline (e.g. booting in a dead zone): leave the queue on disk.
    // Flushing now would hand the events to posthog's in-memory retry queue,
    // which dies with the tab — exactly what the outbox exists to prevent.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    const queue = read();
    if (queue.length === 0) return;
    // Clear FIRST so a capture() throw can't replay the same events forever.
    write([]);
    const now = Date.now();
    for (const item of queue) {
      // Storage is untrusted: skip malformed or expired entries.
      if (typeof item?.event !== 'string' || typeof item?.t !== 'number') {
        continue;
      }
      if (now - item.t > MAX_AGE_MS) continue;
      posthog.capture(
        item.event,
        item.properties && typeof item.properties === 'object'
          ? item.properties
          : undefined,
        { timestamp: new Date(item.t) }
      );
    }
  } catch {
    // Best-effort.
  }
}

let installed = false;
export function installOutboxFlush(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('online', flushOutbox);
  flushOutbox();
}
